/**
 * Iter60.p47 Item C — Search-first Leave Balances lookup.
 *
 * The default Balances tab surface — replaces the dense flat list with a
 * one-employee-at-a-time card. Behaviour spec:
 *
 *   * Empty state on mount: NO backend fetch, just a large search box
 *     and a hint ("Start typing an employee name, EMP code, or Module
 *     ID…"). This keeps the page cheap for HR who typically drill
 *     into a single person at a time.
 *   * As soon as the user types (debounced 300ms) we hit
 *     `GET /api/leave/balances?search=<q>&year=<y>` filtered to the
 *     current module. Results are compact rows the user picks from.
 *   * Clicking a result opens the per-employee card via
 *     `GET /api/leave/balances/employee/{id}` — one card with a small
 *     table of leave-type breakdowns and the employee's Module ID +
 *     EMP code.
 *   * A "Show classic flat list" toggle in the parent tab flips to
 *     the legacy `LeaveBalancesTab` grid.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, User } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import EntityLink from "./EntityLink";
import ModuleIdChip from "./ModuleIdChip";

export default function LeaveBalanceLookup({ moduleId, year }) {
    const { moduleCode: pathModuleCode } = useParams();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState(null); // null = untouched empty state
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [picked, setPicked] = useState(null); // employee_id
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const debounceTimer = useRef(null);
    const fetchToken = useRef(0);

    // Debounced search — no fetch until user actually types.
    useEffect(() => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        const q = query.trim();
        if (!q) {
            setResults(null);
            setLoadingSearch(false);
            return;
        }
        setLoadingSearch(true);
        const token = ++fetchToken.current;
        debounceTimer.current = setTimeout(async () => {
            try {
                const params = { search: q, year };
                if (moduleId) params.module_id = moduleId;
                const { data } = await api.get("/leave/balances", { params });
                if (token !== fetchToken.current) return;
                // Deduplicate by employee_id — the balances endpoint
                // emits one row per (employee, leave_type). For the
                // picker we just want the distinct employees.
                const seen = new Set();
                const distinct = [];
                for (const r of (data || [])) {
                    if (seen.has(r.employee_id)) continue;
                    seen.add(r.employee_id);
                    distinct.push(r);
                }
                setResults(distinct);
            } catch (err) {
                if (token === fetchToken.current) {
                    toast.error(formatApiError(err));
                    setResults([]);
                }
            } finally {
                if (token === fetchToken.current) setLoadingSearch(false);
            }
        }, 300);
        return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
    }, [query, moduleId, year]);

    // Load the picked employee's detail card.
    useEffect(() => {
        if (!picked) { setDetail(null); return; }
        let cancelled = false;
        setLoadingDetail(true);
        api.get(`/leave/balances/employee/${picked}`, { params: { year } })
            .then(({ data }) => { if (!cancelled) setDetail(data); })
            .catch((err) => { if (!cancelled) toast.error(formatApiError(err)); })
            .finally(() => { if (!cancelled) setLoadingDetail(false); });
        return () => { cancelled = true; };
    }, [picked, year]);

    const clear = () => {
        setQuery("");
        setResults(null);
        setPicked(null);
        setDetail(null);
    };

    return (
        <div className="space-y-4" data-testid="leave-balance-lookup">
            {/* Search bar */}
            <div className="asl-card p-5">
                <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--asl-muted)]" />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name or Module ID…"
                            className="w-full h-10 pl-9 pr-9 rounded border text-sm bg-transparent"
                            style={{ borderColor: "var(--asl-border)" }}
                            autoFocus
                            data-testid="bal-lookup-search"
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={clear}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100"
                                aria-label="Clear"
                                data-testid="bal-lookup-clear"
                            >
                                <X className="h-4 w-4 text-[color:var(--asl-muted)]" />
                            </button>
                        )}
                    </div>
                </div>
                {!query.trim() && (
                    <p className="mt-3 text-xs text-[color:var(--asl-muted)]" data-testid="bal-lookup-hint">
                        Start typing to look up a single employee's leave balances.
                        Casual workers are excluded — they are not entitled to leave.
                    </p>
                )}
            </div>

            {/* Search results dropdown */}
            {query.trim() && !picked && (
                <div className="asl-card overflow-hidden" data-testid="bal-lookup-results">
                    {loadingSearch ? (
                        <div className="p-6 text-center text-xs text-[color:var(--asl-muted)]" data-testid="bal-lookup-loading">
                            Searching…
                        </div>
                    ) : (results || []).length === 0 ? (
                        <div className="p-6 text-center text-xs text-[color:var(--asl-muted)]" data-testid="bal-lookup-empty">
                            No employees match. Try a shorter or different search.
                        </div>
                    ) : (
                        <ul>
                            {results.map((r) => (
                                <li key={r.employee_id} className="border-t first:border-t-0" style={{ borderColor: "var(--asl-border)" }}>
                                        <button
                                            type="button"
                                            onClick={() => setPicked(r.employee_id)}
                                            className="w-full text-left px-4 py-3 hover:bg-[color:var(--asl-navy-50,#eef1f8)] focus:bg-[color:var(--asl-navy-50,#eef1f8)] focus:outline-none flex items-center gap-3 transition-colors"
                                            data-testid={`bal-lookup-result-${r.employee_id}`}
                                        >
                                        <ModuleIdChip value={r.module_employee_id} testid={`bal-lookup-modid-${r.employee_id}`} />
                                        <div className="flex-1">
                                            <div className="font-medium asl-navy-text">{r.employee_name}</div>
                                        </div>
                                        <User className="h-4 w-4 text-[color:var(--asl-muted)]" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* Employee detail card */}
            {picked && (
                <div className="asl-card p-5 space-y-4" data-testid="bal-lookup-detail">
                    {loadingDetail || !detail ? (
                        <div className="text-xs text-[color:var(--asl-muted)]">Loading…</div>
                    ) : (
                        <>
                            <div className="flex items-start justify-between gap-3">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <ModuleIdChip value={detail.employee?.module_employee_id} testid="bal-lookup-detail-modid" />
                                        {detail.employee?.id ? (
                                            <EntityLink to={`/m/${pathModuleCode}/staff/${detail.employee.id}`} testid="bal-lookup-detail-name">
                                                <span className="font-semibold text-base asl-navy-text">{detail.employee.full_name}</span>
                                            </EntityLink>
                                        ) : (
                                            <span className="font-semibold text-base asl-navy-text">{detail.employee?.full_name}</span>
                                        )}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setPicked(null)}
                                    className="text-xs text-[color:var(--asl-muted)] hover:asl-navy-text underline"
                                    data-testid="bal-lookup-back"
                                >
                                    ‹ Back to search
                                </button>
                            </div>
                            {/* Iter60.p49 Item E — 5 balance cards, one per leave type,
                                in the mandated order (Annual → Sick → Unpaid →
                                Compassionate → Permission-hourly). Each card shows
                                Entitled + Used prominently; Pending + Available shown
                                as a small footer for context. */}
                            {(() => {
                                const ORDER = ["annual", "sick", "unpaid", "compassionate", "permission_hourly"];
                                const byCode = new Map((detail.balances || []).map((b) => [b.leave_type_code, b]));
                                const ordered = ORDER
                                    .map((c) => byCode.get(c))
                                    .filter(Boolean);
                                // Append any additional non-listed types at the end so
                                // future leave-type additions don't silently disappear.
                                for (const b of (detail.balances || [])) {
                                    if (!ORDER.includes(b.leave_type_code)) ordered.push(b);
                                }
                                return (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="bal-lookup-cards">
                                        {ordered.map((b) => {
                                            const ent = Number(b.entitled_days) || 0;
                                            const used = Number(b.used_days) || 0;
                                            let usedTone = "zero";
                                            if (used > 0 && used < ent) usedTone = "partial";
                                            else if (used > 0 && used >= ent) usedTone = "full";
                                            const tonePalette = {
                                                zero: { bg: "rgba(0,0,0,0.04)", fg: "var(--asl-text-3)", border: "var(--asl-border)" },
                                                partial: { bg: "rgba(217, 119, 6, 0.10)", fg: "#B45309", border: "rgba(217, 119, 6, 0.22)" },
                                                full: { bg: "rgba(168, 50, 74, 0.10)", fg: "var(--asl-crimson)", border: "rgba(168, 50, 74, 0.20)" },
                                            }[usedTone];
                                            return (
                                            <div
                                                key={b.id}
                                                className="border rounded-lg p-3 bg-white flex flex-col gap-2 transition-shadow hover:shadow-sm"
                                                style={{ borderColor: "var(--asl-border)" }}
                                                data-testid={`bal-lookup-card-${b.leave_type_code}`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="text-xs font-semibold asl-navy-text leading-tight">
                                                        {b.leave_type_name}
                                                    </div>
                                                    <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded"
                                                        style={{ background: b.is_paid ? "#DCFCE7" : "#FEE2E2", color: b.is_paid ? "#166534" : "#991B1B" }}>
                                                        {b.is_paid ? "Paid" : "Unpaid"}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 mt-1">
                                                    <div>
                                                        <div className="text-[10px] uppercase font-semibold tracking-wider text-[color:var(--asl-muted)]">Entitled</div>
                                                        <div className="mono tabular-nums text-base font-bold asl-navy-text" data-testid={`bal-lookup-entitled-${b.leave_type_code}`}
                                                             aria-label={`${b.leave_type_name} entitled: ${b.entitled_days}`}>
                                                            {b.entitled_days}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-[10px] uppercase font-semibold tracking-wider text-[color:var(--asl-muted)]">Used</div>
                                                        <div className="mono tabular-nums text-base font-bold flex items-center gap-1.5"
                                                             data-testid={`bal-lookup-used-${b.leave_type_code}`}>
                                                            <span aria-label={`${b.leave_type_name} used: ${b.used_days} of ${b.entitled_days}`}
                                                                  className={`asl-balance-used--${usedTone} px-1.5 rounded`}
                                                                  style={{
                                                                      background: tonePalette.bg,
                                                                      color: tonePalette.fg,
                                                                      border: `1px solid ${tonePalette.border}`,
                                                                  }}>
                                                                {b.used_days}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between text-[10px] text-[color:var(--asl-muted)] pt-1 border-t" style={{ borderColor: "var(--asl-border)" }}>
                                                    <span>Pending <span className="mono tabular-nums">{b.pending_days}</span></span>
                                                    {/* Iter73 — Negative balances render in a red chip so HR can
                                                        immediately spot overrun cases. Monthly accruals absorb
                                                        the deficit until it returns to zero. */}
                                                    {Number(b.available_days) < 0 ? (
                                                        <span>Available{" "}
                                                            <span
                                                                className="mono tabular-nums font-bold"
                                                                style={{
                                                                    background: "rgba(220, 38, 38, 0.10)",
                                                                    color: "#b91c1c",
                                                                    border: "1px solid rgba(220, 38, 38, 0.25)",
                                                                    padding: "1px 6px",
                                                                    borderRadius: 6,
                                                                }}
                                                                title="Balance overdrawn. Auto-absorbs from future monthly accruals."
                                                                data-testid={`bal-lookup-available-${b.leave_type_code}`}
                                                            >{b.available_days}</span>
                                                        </span>
                                                    ) : (
                                                        <span>Available <span className="mono tabular-nums font-bold asl-navy-text" data-testid={`bal-lookup-available-${b.leave_type_code}`}>{b.available_days}</span></span>
                                                    )}
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
